/**
 * The facts line under the deal's name: Request · Asset Type · Purpose ·
 * Priority · State · Target Close · Relationship Owner.
 *
 * Every one of them is a catalogue field, and every one was printed as text
 * whether the dossier was locked or not: a deal received as "Acquisition" that
 * was in fact a refinance had to be corrected under Audit > All fields. Each
 * value is now the field it names, editable in place once unlocked, through
 * the same draft and Save as the rest of the dossier. Locked, the line is
 * exactly what it was.
 *
 * Each value opens its input from the value itself rather than becoming one:
 * the line is one row that shares the header with the action buttons (and, on
 * Parties, the metric bar), and inline inputs — the full "$132,000,000" for
 * "$132M", a select's arrow, a date box for "—" — made it ~210px wider, so at
 * 1366 it wrapped and pushed the whole dossier down 19px. Opened from the
 * value, the line keeps its width and height to the pixel.
 */
import type { SourcedDatapoint } from './workspace/types'
import { EditableValue, showsStandIn } from './EditableValue'
import { useDossierEdit } from './editContext'

export function DossierHeaderFacts({
  meta,
  relationshipOwner,
}: {
  meta: SourcedDatapoint[]
  /** The deal's assignee, when one is recorded. */
  relationshipOwner: string | null
}) {
  const edit = useDossierEdit()
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="dossier-header-facts">
      {meta.map((m) => (
        <div key={m.label} className="text-[10px]">
          <span className="text-stone-600">{m.label}: </span>
          <span className="font-mono text-stone-300">
            {m.fieldName ? (
              <EditableValue
                name={m.fieldName}
                label={m.label}
                display={m.value}
                fields={m.edit}
                popover
                // A request the page sums from initial and future funding,
                // shown while the commitment field holds nothing, is computed:
                // it opens what it is made of, never an empty box for a total.
                computed={
                  m.source?.state === 'derived'
                    ? m.source.formula ?? ''
                    : !m.edit && showsStandIn(edit, m.fieldName, m.value)
                      ? 'shown from other figures while this field itself is empty'
                      : undefined
                }
                inputs={m.inputs}
              />
            ) : (
              m.value
            )}
          </span>
        </div>
      ))}
      {relationshipOwner ? (
        <div className="text-[10px]">
          <span className="text-stone-600">Relationship Owner: </span>
          <span className="text-stone-300">
            <EditableValue name="assignee" label="Relationship Owner" display={relationshipOwner} popover />
          </span>
        </div>
      ) : null}
    </div>
  )
}
